import { AccommodationProduct, AccommodationProductResponse } from "../model";
import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "./ApiErrors";
import { getDivisionById } from "./getDivisionById";

interface SaveResult {
  ok: boolean;
  message?: string;
  errors?: string[];
  productId?: number;
}
import { DivisionId } from 'app/shared/division/getDivisionId';

const parseSaveResponse = async (response: Response | undefined) => {
  if (!response) {
    return { message: undefined, productId: undefined };
  }

  try {
    const data = await response.clone().json();
    if (typeof data === 'string') {
      const numeric = Number(data);
      return {
        message: data,
        productId: Number.isNaN(numeric) ? undefined : numeric,
      };
    }

    if (data && typeof data === 'object') {
      const productId =
        typeof data.productId === 'number'
          ? data.productId
          : undefined;
      const message =
        typeof data.message === 'string'
          ? data.message
          : undefined;

      return { message, productId };
    }
  } catch {
    // fall back to reading as text
  }

  try {
    const text = await response.text();
    const numeric = Number(text);
    return {
      message: text,
      productId: Number.isNaN(numeric) ? undefined : numeric,
    };
  } catch {
    return { message: undefined, productId: undefined };
  }
};

/**
 * Create a new accommodation product
 */
export async function createAccommodationProduct(
  accommodationProduct: AccommodationProduct,
  divisionId: number
): Promise<SaveResult> {
  const localApi = await getLocalApi();

  let divisionName = "";
  try {
    const division = await getDivisionById(divisionId);
    if (division) {
      divisionName = division.name || "";
    }
  } catch {
  }

  const payload: AccommodationProductResponse = {
    productId: 0,
    productName: accommodationProduct.name,
    minimumAge: accommodationProduct.minAge,
    maximumAge: accommodationProduct.maxAge ?? null,
    minimumStay: accommodationProduct.minStay,
    isCommitted: accommodationProduct.isCommitted,
    isNonCommitted: accommodationProduct.isNonCommitted,
    typeId: accommodationProduct.typeId,
    lastModifiedDate: new Date().toISOString(),
    lastModifiedBy: "system",
    lastModifiedById: 1,
    division: divisionName,
    divisionId: divisionId,
    offeringClosureDate: "1000-01-31T00:00:00.000Z",
    entityUpdate: true,
    type: "",
    active: accommodationProduct.active !== false,
  };

  const response = await localApi.call(apiConfig.accommodation_save.replace("{divisionId}", divisionId.toString()), 'PUT', JSON.stringify(payload));
  if (!response) {
    // Provide a default SaveResult in case response is undefined
    return { ok: false, message: 'No response from server', errors: ['No response from server'] };
  }

  const { message, productId } = await parseSaveResponse(response);
  
  let rawText = "";
  try {
    rawText = await response.clone().text();
  } catch {
    rawText = "";
  }

  let parsed: any = null;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = rawText;
    }
  }

  const errors = ApiErrors.extractAllErrors(parsed, response, "Failed to save accommodation product");
  const finalMessage = response.ok
    ? (message || "Accommodation product saved")
    : ApiErrors.formatErrors(errors);

  return { ok: response.ok, message: finalMessage, errors: response.ok ? undefined : errors, productId };
}

/**
 * Update an existing accommodation product
 */
export async function updateAccommodationProduct(
  accommodationProduct: AccommodationProduct,
  divisionId: number
): Promise<SaveResult> {
  const localApi = await getLocalApi();

  const payload: AccommodationProductResponse = {
    productId: Number(accommodationProduct.id),
    productName: accommodationProduct.name,
    minimumAge: accommodationProduct.minAge,
    maximumAge: accommodationProduct.maxAge ?? null,
    minimumStay: accommodationProduct.minStay,
    isCommitted: accommodationProduct.isCommitted,
    isNonCommitted: accommodationProduct.isNonCommitted,
    typeId: accommodationProduct.typeId,
    active: accommodationProduct.active !== false,
  };

    const response = await localApi.call(apiConfig.accommodation_save.replace("{divisionId}", divisionId.toString()), 'PUT', JSON.stringify(payload));
    if (!response) {
        // Provide a default SaveResult in case response is undefined
        return {
            ok: false,
            message: 'No response from server',
            errors: ['No response from server']
        };
    }

    const { message, productId } = await parseSaveResponse(response);
    
    let rawText = "";
    try {
      rawText = await response.clone().text();
    } catch {
      rawText = "";
    }

    let parsed: any = null;
    if (rawText) {
      try {
        parsed = JSON.parse(rawText);
      } catch {
        parsed = rawText;
      }
    }

    const errors = ApiErrors.extractAllErrors(parsed, response, "Failed to save accommodation product");
    const finalMessage = response.ok
      ? (message || "Accommodation product saved")
      : ApiErrors.formatErrors(errors);

    return {
        ok: response.ok,
        message: finalMessage,
        errors: response.ok ? undefined : errors,
        productId: productId ?? Number(accommodationProduct.id)
    };
}

/**
 * Alias for backward compatibility:
 * calling saveAccommodationProduct(...) will update if `id > 0`, otherwise create.
 */
export function saveAccommodationProduct(
  accommodationProduct: AccommodationProduct
): Promise<SaveResult> {
  if (Number(accommodationProduct.id) > 0) {
    return updateAccommodationProduct(accommodationProduct, DivisionId());
  } else {
    return createAccommodationProduct(accommodationProduct, DivisionId());
  }
}